import React, { useEffect, useMemo, useState } from 'react'
import {
  FAX_PREVIEW,
  WEB_PREVIEW,
} from '../../../constants/intakeQue.constants'
import {
  CustomButton,
  Loader,
  MULTI_UPLOAD_FILE_TYPE,
  PdfViewer,
} from '@ui-kit'
import FaxInfo from '../FaxInfo'
import { useParams } from 'react-router-dom'
import { QUEUE_STRINGS } from '../../../utils/queueIntake.types'
import {
  convertS3ToBase64,
  fetchCommunicationIdCorrespondingData,
  fetchHcpFormPdfUrl,
} from '@app-store'
import { FaxDetailsData } from '../../../types/intakeQueue.types'
import { IntakeQueReviewProps } from '../../../utils/types'
import './PopoutWindow.css'
import { fetchDownloadUrlForSingleFIles } from '../../../apis/fetchPresignedUrl.api'
import { EXTENSIONS, INTAKE_DOCUMENT } from '../../../utils/constants'
import FilePreview from '../FilePreview'
import { getContentType, getFileType } from '../../../utils/helperFunctions'

const PopoutWindow: React.FC<IntakeQueReviewProps> = ({ entityName }) => {
  const { communicationId, index, documentId } = useParams<{
    communicationId: string
    index: string
    documentId: string
  }>()
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [faxDetails, setFaxDetails] = useState<FaxDetailsData | null>(null)
  const [faxUrl, setFaxUrl] = useState<string>('')
  const [intakeType, setIntakeType] = useState<string>()
  const [isAePcStatusVisible, setIsAePcStatusVisible] = useState<boolean>(false)
  const [selectedFileType, setSelectedFileType] = useState<string>('')
  const [selectedFilePath, setSelectedFilePath] = useState<string>('')
  const [docxPreviewUrl, setDocxPreviewUrl] = useState<string>('')
  const fetchFaxDetails = async () => {
    try {
      const payload = {
        entity_name: QUEUE_STRINGS?.COMMUNICATION_LOWERCASE,
        select: [
          'programs',
          'created_at',
          'sender_name',
          'communication_id',
          'total_pages',
          'users',
          'category_communication_type',
          'document',
          'c_ae_pc_document',
        ],
        related_entities: {
          users: {
            select: ['first_name', 'last_name', 'email'],
          },
          document: {
            select: ['attachment'],
          },
          c_ae_pc_document: {
            select: ['ae_pc_status'],
          },
        },
      }
      const response = await fetchCommunicationIdCorrespondingData(
        parseInt(communicationId ?? ''),
        payload
      )
      setFaxDetails(response?.data)
      if (response?.data?.c_ae_pc_document) {
        setIsAePcStatusVisible(true)
      }
    } catch (error) {
      console.error(error)
    }
  }
  const fetchDocumentDetails = async () => {
    try {
      const payload = {
        entity_name: QUEUE_STRINGS?.AE_PC_DOCUMENT,
        select: [
          'total_pages',
          'users',
          'document',
          'programs',
          'created_at',
          'documents',
        ],
        related_entities: {
          users: {
            select: ['first_name', 'last_name', 'email'],
          },
          documents: {
            select: ['attachment', 'document_key'],
          },
        },
      }
      const response = await fetchCommunicationIdCorrespondingData(
        parseInt(documentId ?? ''),
        payload
      )
      setFaxDetails(response?.data)
    } catch (error) {
      console.error(error)
    }
  }
  const fetchWebDetails = async () => {
    const payload = {
      entity_name: QUEUE_STRINGS?.HCP_PORTAL_ENTITY_LOWERCASE,
      select: [
        'name',
        'hcp_program',
        'created_at',
        'received_from',
        'enrollment_id',
        'sender_name',
        'users',
      ],
      related_entities: {
        users: {
          select: ['first_name', 'last_name', 'email'],
        },
      },
    }

    try {
      const response = await fetchCommunicationIdCorrespondingData(
        parseInt(communicationId ?? ''),
        payload
      )
      setFaxDetails(response?.data)
    } catch (error) {
      console.error(error)
    }
  }

  const fetchCommunicationDetails = async () => {
    if (entityName === QUEUE_STRINGS?.HCP_PORTAL_ENTITY_LOWERCASE) {
      await fetchWebDetails()
      setIntakeType(QUEUE_STRINGS.WEB)
    } else if (entityName === INTAKE_DOCUMENT.DOCUMENT) {
      await fetchDocumentDetails()
      setIntakeType(QUEUE_STRINGS.DOCUMENT_TYPE)
    } else {
      await fetchFaxDetails()
      setIntakeType(QUEUE_STRINGS.FAX)
    }
    await getFaxUrl()
  }

  const fetchDocumentUrl = async () => {
    try {
      const attachmentsData = faxDetails?.documents?.attachment
      if (attachmentsData?.length) {
        const { path, name } = attachmentsData[Number(index) ?? 0]
        const response = await fetchDownloadUrlForSingleFIles(path, name)
        setFaxUrl(response.data.url)
        if (response) {
          setIsLoading(false)
          setSelectedFileType(getContentType(attachmentsData[Number(index)]))
          setSelectedFilePath(path + '/' + name)
        }
      }
    } catch (err) {
      console.log(err)
    }
  }

  const getFaxUrl = async () => {
    // await setIsLoading(true)
    try {
      if (intakeType === QUEUE_STRINGS.FAX) {
        const attachmentsData = faxDetails?.document?.attachment
        if (attachmentsData?.length) {
          const { path, name } = attachmentsData[Number(index) ?? 0]
          const response = await fetchDownloadUrlForSingleFIles(path, name)
          setFaxUrl(response.data.url)
          if (response) {
            setIsLoading(false)
          }
        }
      } else if (intakeType === QUEUE_STRINGS.WEB) {
        const response = await fetchHcpFormPdfUrl(
          faxDetails?.enrollment_id ?? ''
        )
        setFaxUrl(response?.data?.url)
        if (response) {
          setIsLoading(false)
        }
      } else if (intakeType === QUEUE_STRINGS.DOCUMENT_TYPE) {
        fetchDocumentUrl()
      }
    } catch (error) {
      setIsLoading(false)
    }
  }
  const close = () => {
    window.close()
  }

  useEffect(() => {
    fetchCommunicationDetails()
  }, [communicationId, documentId])

  useEffect(() => {
    getFaxUrl()
  }, [intakeType])

  const fileType = useMemo(
    () => getFileType(selectedFileType, faxUrl),
    [selectedFileType, faxUrl]
  )

  useEffect(() => {
    const fetchBase64Data = async () => {
      if (
        selectedFilePath &&
        (fileType === EXTENSIONS.DOCX || fileType === EXTENSIONS.DOC)
      ) {
        try {
          const response = await convertS3ToBase64(selectedFilePath)

          const base64Data = response.data
          const byteCharacters = atob(base64Data)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], {
            type:
              fileType === EXTENSIONS.DOCX
                ? MULTI_UPLOAD_FILE_TYPE.DOCX
                : MULTI_UPLOAD_FILE_TYPE.DOC,
          })
          const blobUrl = URL.createObjectURL(blob)
          setDocxPreviewUrl(blobUrl)
        } catch (error) {
          console.error('Error fetching base64 data:', error)
        }
      }
    }

    fetchBase64Data()

    return () => {
      if (docxPreviewUrl) {
        URL.revokeObjectURL(docxPreviewUrl)
      }
    }
  }, [selectedFilePath, fileType])

  return isLoading ? (
    <Loader isGlobal={true} loaderHeight="126" loaderWidth="126" />
  ) : (
    <div className="max-w-[44.75rem] my-0 mx-auto mb-4 overflow-hidden border border-[#c8c8c8] rounded-1.5">
      <div className="p-2 border-b border-b-[#d9d9d9]">
        <div className="flex justify-between">
          <h2 className="text-base leading-6 font-semibold">
            {intakeType === QUEUE_STRINGS?.WEB
              ? `${WEB_PREVIEW.ENROLLMENT_ID} - ${faxDetails?.enrollment_id}`
              : intakeType === QUEUE_STRINGS.DOCUMENT_TYPE
              ? `${INTAKE_DOCUMENT.DOCUMENT_ID} - ${faxDetails?.documents?.document_key}`
              : faxDetails?.category_communication_type?.label ===
                QUEUE_STRINGS.MAIL
              ? `Mail ID - ${faxDetails?.communication_id}`
              : `${FAX_PREVIEW.FAX_ID} - ${faxDetails?.communication_id}`}
          </h2>
          <CustomButton
            type="button"
            color="others"
            widthClass="w-[4.13rem]"
            className="h-[1.875rem] gap-2 text-white rounded-tl-[4px] bg-primary-500 shadow-[0px_2px_12px_0px_#0000001F] download"
            isFilled={true}
            onClick={() => close()}
            data-qa-id="close-portal-window"
          >
            {FAX_PREVIEW.CLOSE}
          </CustomButton>
        </div>
        <FaxInfo
          faxData={faxDetails}
          parentEntity={
            intakeType === QUEUE_STRINGS.DOCUMENT_TYPE
              ? INTAKE_DOCUMENT.DOCUMENT
              : ''
          }
          isAePcStatusVisible={isAePcStatusVisible}
          intakeType={intakeType}
        />
      </div>
      <div className="w-[100%] pdf-wrap relative">
        {entityName === INTAKE_DOCUMENT.DOCUMENT ? (
          <FilePreview
            url={faxUrl}
            fileType={fileType}
            docxPreviewUrl={docxPreviewUrl}
          />
        ) : (
          <PdfViewer pdfUrl={faxUrl} />
        )}
      </div>
    </div>
  )
}

export default PopoutWindow
